import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Clientes from './pages/Clientes';
import NuevoCliente from './pages/NuevoCliente';
import MovimientosPage from './pages/Movimientos';
import HistoricoPage from './pages/Historico';
import DetallesMovimientosPage from './pages/DetallesMovimientos';
import Reservas from './pages/Reservas';
import Ventas from './pages/Ventas';
import Restaurante from './pages/Restaurante';
import RestauranteCocina from './pages/RestauranteCocina';
import RestauranteMesa from './pages/RestauranteMesa';
import MozoApp from './mozo/MozoApp';
import RestauranteHistorial from './pages/RestauranteHistorial';
import RestauranteConfiguracion from './pages/RestauranteConfiguracion';
import CantinaProductos from './pages/CantinaProductos';
import CantinaHistorial from './pages/CantinaHistorial';
import CantinaCierre from './pages/CantinaCierre';
import CajaCierre from './pages/CajaCierre';
import Facturacion from './pages/Facturacion';
import Clima from './pages/Clima';
import Dashboard from './pages/Dashboard';
import DetallesClientesPage from './pages/DetallesClientes';
import HelpPage from './pages/HelpPage';
import Registros from './pages/Registros';
import { ToastContainer } from 'react-toastify';
import HistoricoMovimientosPage from './pages/HistoricoMovimientosPage';
import HistoricoRegistrosPage from './pages/HistoricoRegistrosPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import HistoricoVentasPorUsuarioPage from './pages/HistoricoVentasPorUsuarioPage';
import Guarderia from './pages/Guarderia';
import CampUIKit from './pages/CampUIKit';
import { api } from './lib/api';
import ModalPassword from './components/registrosIngresos/ModalPassword';

const AUTH_STORAGE_KEY = 'sensitiveRouteAuth';
const AUTH_WINDOW_MS = 1000 * 60 * 30;

const leerAutorizacion = () => {
  try {
    const raw = window.sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || Date.now() > Number(parsed.expiresAt)) {
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const guardarAutorizacion = (autorizador) => {
  const payload = {
    autorizador,
    expiresAt: Date.now() + AUTH_WINDOW_MS,
  };
  window.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
};

const SensitiveRoute = ({ children }) => {
  const location = useLocation();
  const [estado, setEstado] = useState(() => (leerAutorizacion() ? 'ok' : 'pending'));
  const [password, setPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [validandoPin, setValidandoPin] = useState(false);

  useEffect(() => {
    const autorizar = async () => {
      const existente = leerAutorizacion();
      if (existente) {
        setEstado('ok');
        return;
      }

      setPassword('');
      setModalError('');
      setEstado('awaiting_pin');
    };

    autorizar();

  }, [location.pathname]);

  const confirmarPin = async () => {
    const pin = password.toString().trim();
    if (!pin) {
      setModalError('El PIN es obligatorio.');
      return;
    }
    setValidandoPin(true);
    setModalError('');
    try {
      const response = await api.post('/usuarios/verificar-autorizacion', {
        password: pin,
      });
      guardarAutorizacion(response?.data?.autorizador || null);
      setEstado('ok');
      setPassword('');
    } catch (error) {
      setModalError(error?.response?.data?.error || 'PIN inválido para acceso restringido.');
    } finally {
      setValidandoPin(false);
    }
  };

  const cancelarPin = () => {
    setPassword('');
    setModalError('');
    setEstado('denied');
  };

  if (estado === 'pending') {
    return null;
  }

  if (estado === 'awaiting_pin') {
    return (
      <ModalPassword
        showPasswordModal
        ventaPendiente={null}
        password={password}
        setPassword={setPassword}
        enviandoVenta={validandoPin}
        confirmarVentaConPassword={confirmarPin}
        onCancel={cancelarPin}
        title="Acceso restringido"
        description="Ingrese PIN de autorización admin"
        details="Este módulo requiere autorización de un usuario administrador."
        errorMessage={modalError}
      />
    );
  }

  if (estado === 'denied') {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/nuevocliente" element={<NuevoCliente />} />
        <Route path="/movimientos" element={<MovimientosPage />} />
        <Route path="/historico" element={<SensitiveRoute><HistoricoPage /></SensitiveRoute>} />
        <Route path="/detallesmovimientos" element={<DetallesMovimientosPage />} />
        <Route path="/reservas" element={<Reservas />} />
        <Route path="/ventas" element={<Ventas />} />
        <Route path="/restaurante" element={<Restaurante />} />
        <Route path="/restaurante/cocina" element={<RestauranteCocina />} />
        <Route path="/restaurante/historial" element={<SensitiveRoute><RestauranteHistorial /></SensitiveRoute>} />
        <Route path="/restaurante/configuracion" element={<SensitiveRoute><RestauranteConfiguracion /></SensitiveRoute>} />
        <Route path="/restaurante/mesa/:id" element={<RestauranteMesa />} />
        <Route path="/mozo/*" element={<MozoApp />} />
        <Route path="/cantina/productos" element={<CantinaProductos />} />
        <Route path="/cantina/historial" element={<CantinaHistorial />} />
        <Route path="/cantina/cierre" element={<SensitiveRoute><CantinaCierre /></SensitiveRoute>} />
        <Route path="/caja/cierre" element={<SensitiveRoute><CajaCierre /></SensitiveRoute>} />
        <Route path="/facturacion" element={<Facturacion />} />
        <Route path="/clima" element={<Clima />} />
        <Route path="/dashboard" element={<SensitiveRoute><Dashboard /></SensitiveRoute>} />
        <Route path="/detallesclientes/:id" element={<DetallesClientesPage />} />
        <Route path="/ayuda" element={<HelpPage />} />
        <Route path="/registros" element={<Registros />} />
        <Route path="/historico-movimientos" element={<SensitiveRoute><HistoricoMovimientosPage /></SensitiveRoute>} />
        <Route path="/historico-registros" element={<SensitiveRoute><HistoricoRegistrosPage /></SensitiveRoute>} />
        <Route path="/configuracionPage" element={<SensitiveRoute><ConfiguracionPage /></SensitiveRoute>} />
        <Route path="/hsitorico-ventas-por-usuario" element={<SensitiveRoute><HistoricoVentasPorUsuarioPage /></SensitiveRoute>} />
        <Route path="/historico-ventas-por-usuario" element={<SensitiveRoute><HistoricoVentasPorUsuarioPage /></SensitiveRoute>} />
        <Route path="/guarderia" element={<Guarderia />} />
        <Route path="/ui-kit" element={<CampUIKit />} />
      </Routes>
      <ToastContainer />
    </div>
  );
}

export default App;
